import {PageCardWrapper} from '@components/ui/page-atoms/PageAtoms.tsx'
import {Helmet} from 'react-helmet'
import {useTranslation} from '@/translations/i18n.tsx'
import {Divider} from '@components/ui/divider/Divider.tsx'
import {CreateProjectFormProvider} from '@/features/projects/components/project-form/create-project-form-provider/CreateProjectFormProvider'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {CreateProjectUrlParams} from '@/features/projects/types'
import {useGetPatient} from '@/features/patients/services/useGetPatient'
import {ProjectFormHeadingLoader} from '@/features/projects/components/project-form/project-form-heading/ProjectFormHeadingLoader'
import {FormSectionLoader} from '@/components/commons/form-section/FormSectionLoader'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {CreateProjectFormCard} from '@/features/projects/components/project-form/create-project-form-card/CreateProjectFormCard'
import {useGetPatientLastInfo} from '@/features/patients/services/useGetPatientLastInfo'
import {useGetSpecialist} from '@/features/projects/specialists/queries/useGetSpecialist'
import {ResumeProjectFormCard} from '@/features/projects/components/resume-project-form-card/ResumeProjectFormCard'
import {PatientExtended, PatientLastInfo} from '@/features/patients/types'
import {ResumeProjectFormValidationSchema} from '@/features/projects/formSchemas'
import {ResumeProjectFormProvider} from '@/features/projects/components/project-form/resume-project-form-provider/ResumeProjectFormProvider'
import {useGetHolidays} from '@/features/projects/queries/useGetHolidays'
import dayjs from 'dayjs'
import {usePermissions} from '@/features/auth/store/store'
import {NoPermissionBox} from '@/components/commons/no-permission-box/NoPermissionBox'
import {ReferringSpecialist} from '@/features/referring-specialists/types'

export const CreateProject = () => {
    const {t} = useTranslation()
    const permissions = usePermissions()
    const {searchParams} = useParsedSearchParams(CreateProjectUrlParams)

    const {
        data: patient,
        isLoading: isLoadingPatient,
        isError: isErrorPatient
    } = useGetPatient({
        patientId: searchParams.patientId ?? undefined,
        responseType: 'extended',
        options: {
            enabled: !!searchParams.patientId
        }
    })

    const {
        data: lastPatientInfo,
        isLoading: isLoadingLastPatientInfo,
        isError: isErrorLastPatientInfo
    } = useGetPatientLastInfo({
        patientId: searchParams.patientId ?? undefined,
        type: patient?.treatmentRequest?.type as 'cycle_resumption' | 'treatment_resumption',
        options: {
            enabled:
                !!searchParams.patientId &&
                (patient?.treatmentRequest?.type == 'cycle_resumption' ||
                    patient?.treatmentRequest?.type == 'treatment_resumption')
        }
    })

    const {data: holidays} = useGetHolidays({
        urlParams: {year: dayjs(lastPatientInfo?.project?.startDate).year()},
        options: {
            enabled: !!lastPatientInfo?.project?.startDate
        }
    })

    const {
        data: specialist,
        isLoading: isLoadingSpecialist,
        isError: isErrorSpecialist
    } = useGetSpecialist({
        urlParams: {
            id: lastPatientInfo?.project?.specialistId ?? ''
        },
        options: {
            enabled: !!lastPatientInfo?.project?.specialistId
        }
    })

    const isLoading =
        (searchParams.origin == 'treatment_request' && (isLoadingLastPatientInfo || isLoadingSpecialist)) ||
        isLoadingPatient

    const isError = isErrorPatient || isErrorLastPatientInfo || isErrorSpecialist

    if (!permissions?.projects.write) {
        return <NoPermissionBox />
    }

    return (
        <>
            <Helmet title={t('projects:create_meta_title')} />
            {isLoading ? (
                <PageCardWrapper>
                    <ProjectFormHeadingLoader />
                    <Divider direction="horizontal" />
                    <FormSectionLoader />
                </PageCardWrapper>
            ) : isError ? (
                <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
            ) : (
                <CreateProjectFormController
                    patient={patient}
                    lastProject={lastPatientInfo?.project}
                    specialist={specialist}
                    holidays={holidays}
                />
            )}
        </>
    )
}

const CreateProjectFormController = ({
    patient,
    lastProject,
    specialist,
    holidays
}: {
    patient?: PatientExtended
    isLoadingPatient?: boolean
    lastProject?: PatientLastInfo['project']
    specialist?: ReferringSpecialist
    holidays?: {date: string}[]
}) => {
    const {t} = useTranslation()
    const isResumption =
        patient?.treatmentRequest?.type == 'cycle_resumption' ||
        patient?.treatmentRequest?.type == 'treatment_resumption'

    if (isResumption && lastProject && patient?.treatmentRequest && patient && specialist) {
        return (
            <ResumeProjectFormProvider
                project={lastProject}
                treatmentRequestId={patient.treatmentRequest.id}
                patient={patient}
                specialist={specialist}
                holidays={holidays || []}
            >
                <ResumeProjectFormCard
                    title={t('projects:add_new_project')}
                    schema={ResumeProjectFormValidationSchema}
                    projectId={lastProject.id}
                />
            </ResumeProjectFormProvider>
        )
    }

    return (
        <CreateProjectFormProvider defaultPatient={patient}>
            <CreateProjectFormCard />
        </CreateProjectFormProvider>
    )
}
